"use client";

import { useRef, useState } from "react";
import { Check, LayoutGrid, Pipette, Plus, RefreshCw, Settings, Trash2, X } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import LomdaUploadFile from "../shared/lomdaUploadFile";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    HotspotBlock as HotspotBlockType,
    HotspotMarker,
    HotspotTextPosition,
    ImageFill,
    MediaAspect,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { HOTSPOT_COLORS, createHotspotMarker } from "../../../lib/blockRegistry";
import { calloutClasses, tailClasses } from "../../../lib/walkthrough";
import { MEDIA_ASPECTS, aspectLabel } from "../../../lib/mediaAspect";
import {
    LOMDA_DROPZONE_CLASS,
    LOMDA_DROPZONE_DRAG_CLASS,
} from "../shared/dropzone";
import { IMAGE_UPLOAD } from "../../../lib/uploadLimits";

const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(max, v));

const POSITIONS: HotspotTextPosition[] = [
    "auto",
    "top",
    "bottom",
    "start",
    "end",
    "none",
];

const ASPECT_CLASS: Record<MediaAspect, string> = {
    auto: "",
    "16:9": "aspect-video",
    "4:3": "aspect-[4/3]",
    "1:1": "aspect-square",
};
const GRID_STYLE = {
    backgroundImage:
        "linear-gradient(to right, rgba(0,0,0,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.08) 1px, transparent 1px)",
    backgroundSize: "10% 10%",
};

function imageClasses(aspect: MediaAspect, fit: ImageFill) {
    if (aspect === "auto") return "w-full";
    return cn(
        "absolute inset-0 h-full w-full",
        fit === "cover" ? "object-cover" : "object-contain"
    );
}

function PreviewHotspots({ data }: { data: HotspotBlockType["data"] }) {
    const [openId, setOpenId] = useState<string | null>(null);
    const { image, alt, aspect, fit, markers } = data;
    if (!image) return null;

    return (
        <div
            className={cn(
                "relative overflow-hidden rounded-xl border bg-muted shadow-sm",
                aspect !== "auto" && ASPECT_CLASS[aspect]
            )}
        >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={image} alt={alt} className={imageClasses(aspect, fit)} />

            {markers.map((m) => {
                const open = openId === m.id;
                return (
                    <button
                        key={m.id}
                        type="button"
                        onClick={() => setOpenId(open ? null : m.id)}
                        style={{
                            left: `${m.x}%`,
                            top: `${m.y}%`,
                            width: `${m.width}%`,
                            height: `${m.height}%`,
                            borderColor: m.color,
                            boxShadow: open
                                ? `0 0 0 2px #fff, 0 0 0 4px ${m.color}`
                                : `0 0 16px ${m.color}cc`,
                        }}
                        className="absolute rounded-md border-2 transition hover:bg-primary/10"
                    >
                        <span
                            style={{ backgroundColor: m.color }}
                            className="absolute left-1/2 top-1/2 flex size-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-primary-foreground shadow"
                        >
                            <Plus className="size-3.5" />
                        </span>
                        {open && m.label && m.textPosition !== "none" && (
                            <span
                                style={{ borderColor: m.color }}
                                className={cn(
                                    "lomda-v3-card-text absolute z-10 w-44 rounded-lg border bg-card p-2 text-start lomda-v3-text-small shadow-md",
                                    calloutClasses(m.textPosition)
                                )}
                            >
                                {m.label}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

export default function HotspotBlock({ block }: { block: HotspotBlockType }) {
    const patchBlockData = useLomdaV3Editor((s) => s.patchBlockData);

    const imageRef = useRef<HTMLDivElement>(null);
    const [selected, setSelected] = useState<string | null>(null);
    const [settingsFor, setSettingsFor] = useState<string | null>(null);
    const [showGrid, setShowGrid] = useState(false);
    const [draft, setDraft] = useState<{
        x: number;
        y: number;
        width: number;
        height: number;
    } | null>(null);

    const drag = useRef<{
        mode: "draw" | "move" | "resize";
        id?: string;
        startX: number;
        startY: number;
        orig?: { x: number; y: number; width: number; height: number };
    } | null>(null);

    const editable = useBlockEditable();
    const { image, alt, aspect, fit, markers } = block.data;

    const update = (patch: Partial<HotspotBlockType["data"]>) =>
        patchBlockData(block.id, "hotspot", () => patch);
    const updateMarker = (id: string, patch: Partial<HotspotMarker>) =>
        update({
            markers: markers.map((m) => (m.id === id ? { ...m, ...patch } : m)),
        });
    const removeMarker = (id: string) => {
        update({ markers: markers.filter((m) => m.id !== id) });
        setSelected(null);
    };

    const pct = (clientX: number, clientY: number) => {
        const rect = imageRef.current!.getBoundingClientRect();
        return {
            x: ((clientX - rect.left) / rect.width) * 100,
            y: ((clientY - rect.top) / rect.height) * 100,
        };
    };
    const capture = (e: React.PointerEvent) =>
        imageRef.current?.setPointerCapture(e.pointerId);

    const onBackgroundDown = (e: React.PointerEvent) => {
        const p = pct(e.clientX, e.clientY);
        drag.current = { mode: "draw", startX: p.x, startY: p.y };
        setSelected(null);
        setDraft({ x: p.x, y: p.y, width: 0, height: 0 });
        capture(e);
    };
    const startMove = (e: React.PointerEvent, m: HotspotMarker) => {
        e.stopPropagation();
        setSelected(m.id);
        const p = pct(e.clientX, e.clientY);
        drag.current = {
            mode: "move",
            id: m.id,
            startX: p.x,
            startY: p.y,
            orig: { x: m.x, y: m.y, width: m.width, height: m.height },
        };
        capture(e);
    };
    const startResize = (e: React.PointerEvent, m: HotspotMarker) => {
        e.stopPropagation();
        const p = pct(e.clientX, e.clientY);
        drag.current = {
            mode: "resize",
            id: m.id,
            startX: p.x,
            startY: p.y,
            orig: { x: m.x, y: m.y, width: m.width, height: m.height },
        };
        capture(e);
    };
    const onMove = (e: React.PointerEvent) => {
        const d = drag.current;
        if (!d) return;
        const p = pct(e.clientX, e.clientY);
        if (d.mode === "draw") {
            setDraft({
                x: Math.min(d.startX, p.x),
                y: Math.min(d.startY, p.y),
                width: Math.abs(p.x - d.startX),
                height: Math.abs(p.y - d.startY),
            });
        } else if (d.mode === "move" && d.id && d.orig) {
            updateMarker(d.id, {
                x: clamp(d.orig.x + (p.x - d.startX), 0, 100 - d.orig.width),
                y: clamp(d.orig.y + (p.y - d.startY), 0, 100 - d.orig.height),
            });
        } else if (d.mode === "resize" && d.id && d.orig) {
            updateMarker(d.id, {
                width: clamp(d.orig.width + (p.x - d.startX), 4, 100 - d.orig.x),
                height: clamp(d.orig.height + (p.y - d.startY), 4, 100 - d.orig.y),
            });
        }
    };
    const onUp = () => {
        const d = drag.current;
        drag.current = null;
        if (d?.mode === "draw" && draft) {
            if (draft.width >= 4 && draft.height >= 4) {
                const m = createHotspotMarker(
                    draft.x,
                    draft.y,
                    draft.width,
                    draft.height
                );
                update({ markers: [...markers, m] });
                setSelected(m.id);
            }
            setDraft(null);
        }
    };

    if (!editable) return <PreviewHotspots data={block.data} />;

    if (!image) {
        return (
            <LomdaUploadFile
                reuse="image"
                reuseLayout="canvas"
                options={{
                    ...IMAGE_UPLOAD,
                    className:
                        `${LOMDA_DROPZONE_CLASS} h-64 w-full rounded-xl`,
                    dragClassName: LOMDA_DROPZONE_DRAG_CLASS,
                    title: {
                        text: t("lomda-v3-editor.media.upload-image"),
                        keyName: "lomda-v3-editor.media.upload-image",
                    },
                    subtitle: {
                        text: t("lomda-v3-editor.media.drop-hint"),
                        keyName: "lomda-v3-editor.media.drop-hint",
                    },
                }}
                onUploadFinished={(file) => update({ image: file.fileUrl })}
            />
        );
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={() => update({ image: "", markers: [] })}
                    className="flex items-center gap-1.5 rounded-lg border bg-card px-2 py-1 lomda-v3-text-small text-muted-foreground transition hover:text-[hsl(var(--lomda-card-text))]"
                >
                    <RefreshCw className="size-3.5" />
                    {t("lomda-v3-editor.walkthrough.change-image")}
                </button>
                <input
                    value={alt}
                    onChange={(e) => update({ alt: e.target.value })}
                    placeholder={t("lomda-v3-editor.walkthrough.alt-text")}
                    className="h-8 w-40 rounded-lg border px-2 lomda-v3-text-small outline-none"
                />
                <select
                    value={aspect}
                    onChange={(e) =>
                        update({ aspect: e.target.value as MediaAspect })
                    }
                    className="lomda-v3-card-text h-8 rounded-lg border bg-card px-2 lomda-v3-text-small outline-none"
                >
                    {MEDIA_ASPECTS.map((a) => (
                        <option key={a} value={a}>
                            {t("lomda-v3-editor.media.aspect")}: {aspectLabel(a)}
                        </option>
                    ))}
                </select>
                {aspect !== "auto" && (
                    <button
                        type="button"
                        onClick={() =>
                            update({
                                fit: (fit === "cover"
                                    ? "contain"
                                    : "cover") as ImageFill,
                            })
                        }
                        className="rounded-lg border bg-card px-2 py-1.5 lomda-v3-text-small text-muted-foreground transition hover:text-[hsl(var(--lomda-card-text))]"
                    >
                        {fit === "cover"
                            ? t("lomda-v3-editor.block.image.fill-cover")
                            : t("lomda-v3-editor.block.image.fill-contain")}
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => setShowGrid((g) => !g)}
                    title={t("lomda-v3-editor.walkthrough.grid")}
                    className={cn(
                        "flex size-8 items-center justify-center rounded-lg border bg-card transition",
                        showGrid
                            ? "border-primary text-primary"
                            : "text-muted-foreground hover:text-[hsl(var(--lomda-card-text))]"
                    )}
                >
                    <LayoutGrid className="size-4" />
                </button>
            </div>
            <span className="lomda-v3-text-small text-muted-foreground">
                {t("lomda-v3-editor.walkthrough.hotspot-draw-hint")}
            </span>

            <div
                ref={imageRef}
                onPointerDown={onBackgroundDown}
                onPointerMove={onMove}
                onPointerUp={onUp}
                className={cn(
                    "relative cursor-crosshair touch-none select-none overflow-hidden rounded-xl border bg-muted",
                    aspect !== "auto" && ASPECT_CLASS[aspect]
                )}
            >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                    src={image}
                    alt={alt}
                    className={cn("pointer-events-none select-none", imageClasses(aspect, fit))}
                    draggable={false}
                />
                {showGrid && (
                    <div
                        className="pointer-events-none absolute inset-0 z-[5]"
                        style={GRID_STYLE}
                    />
                )}

                {markers.map((m) => (
                    <div
                        key={m.id}
                        onPointerDown={(e) => startMove(e, m)}
                        style={{
                            left: `${m.x}%`,
                            top: `${m.y}%`,
                            width: `${m.width}%`,
                            height: `${m.height}%`,
                            borderColor: m.color,
                            boxShadow:
                                selected === m.id
                                    ? `0 0 0 2px #fff, 0 0 0 4px ${m.color}`
                                    : `0 0 10px ${m.color}99`,
                        }}
                        className="absolute cursor-move rounded-md border-2 bg-primary/5"
                    >
                        {m.textPosition !== "none" && (
                            <div
                                style={{ borderColor: m.color }}
                                className={cn(
                                    "lomda-v3-card-text absolute z-10 w-44 rounded-lg border bg-card p-2 shadow-md",
                                    calloutClasses(m.textPosition)
                                )}
                            >
                                <textarea
                                    value={m.label}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onChange={(e) =>
                                        updateMarker(m.id, { label: e.target.value })
                                    }
                                    placeholder={t("lomda-v3-editor.walkthrough.hotspot-text")}
                                    rows={2}
                                    className="w-full resize-none bg-transparent lomda-v3-text-small outline-none"
                                />
                                <span
                                    style={{ backgroundColor: "#fff", borderColor: m.color }}
                                    className={cn(
                                        "absolute size-2.5 rotate-45 border",
                                        tailClasses(m.textPosition)
                                    )}
                                />
                            </div>
                        )}

                        <Popover
                            open={settingsFor === m.id}
                            onOpenChange={(o) => setSettingsFor(o ? m.id : null)}
                        >
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    onPointerDown={(e) => {
                                        e.stopPropagation();
                                        setSelected(m.id);
                                    }}
                                    title={t("lomda-v3-editor.walkthrough.hotspot-settings")}
                                    className="absolute -end-3 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full border bg-card text-muted-foreground shadow transition hover:text-[hsl(var(--lomda-card-text))]"
                                >
                                    <Settings className="size-3.5" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent
                                align="start"
                                className="w-64 p-3"
                                onPointerDown={(e) => e.stopPropagation()}
                            >
                                <div className="mb-2 flex items-center justify-between">
                                    <span className="lomda-v3-text-label">
                                        {t("lomda-v3-editor.walkthrough.hotspot-settings")}
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <button
                                            type="button"
                                            aria-label={t("lomda-v3-editor.block.delete")}
                                            onClick={() => removeMarker(m.id)}
                                            className="flex size-6 items-center justify-center rounded text-muted-foreground transition hover:text-destructive"
                                        >
                                            <Trash2 className="size-4" />
                                        </button>
                                        <button
                                            type="button"
                                            aria-label="close"
                                            onClick={() => setSettingsFor(null)}
                                            className="flex size-6 items-center justify-center rounded text-muted-foreground transition hover:text-foreground"
                                        >
                                            <X className="size-4" />
                                        </button>
                                    </div>
                                </div>

                                <div className="mb-3 flex flex-col gap-1">
                                    <span className="lomda-v3-text-small text-muted-foreground">
                                        {t("lomda-v3-editor.walkthrough.text-position")}
                                    </span>
                                    <select
                                        value={m.textPosition}
                                        onChange={(e) =>
                                            updateMarker(m.id, {
                                                textPosition: e.target.value as HotspotTextPosition,
                                            })
                                        }
                                        className="lomda-v3-card-text h-8 w-full rounded-lg border bg-card px-2 lomda-v3-text-small outline-none"
                                    >
                                        {POSITIONS.map((p) => (
                                            <option key={p} value={p}>
                                                {t(`lomda-v3-editor.walkthrough.pos-${p}`)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <span className="lomda-v3-text-small text-muted-foreground">
                                        {t("lomda-v3-editor.walkthrough.highlight-color")}
                                    </span>
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        {HOTSPOT_COLORS.map((c) => (
                                            <button
                                                key={c}
                                                type="button"
                                                onClick={() => updateMarker(m.id, { color: c })}
                                                style={{ backgroundColor: c }}
                                                className={cn(
                                                    "flex size-6 items-center justify-center rounded-full border-2 transition",
                                                    m.color === c
                                                        ? "border-foreground"
                                                        : "border-transparent"
                                                )}
                                            >
                                                {m.color === c && (
                                                    <Check className="size-3.5 text-primary-foreground" />
                                                )}
                                            </button>
                                        ))}
                                        <label
                                            title={t("lomda-v3-editor.walkthrough.custom-color")}
                                            className="relative flex size-6 cursor-pointer items-center justify-center rounded-full border text-muted-foreground"
                                        >
                                            <Pipette className="size-3.5" />
                                            <input
                                                type="color"
                                                value={m.color}
                                                onChange={(e) =>
                                                    updateMarker(m.id, { color: e.target.value })
                                                }
                                                className="absolute inset-0 cursor-pointer opacity-0"
                                            />
                                        </label>
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>

                        {selected === m.id && (
                            <span
                                onPointerDown={(e) => startResize(e, m)}
                                style={{ backgroundColor: m.color }}
                                className="absolute -bottom-1.5 -end-1.5 size-3 cursor-nwse-resize rounded-full border-2 border-white"
                            />
                        )}
                    </div>
                ))}

                {draft && (
                    <div
                        style={{
                            left: `${draft.x}%`,
                            top: `${draft.y}%`,
                            width: `${draft.width}%`,
                            height: `${draft.height}%`,
                            borderColor: HOTSPOT_COLORS[0],
                        }}
                        className="absolute rounded-md border-2 border-dashed bg-primary/5"
                    />
                )}
            </div>
        </div>
    );
}
